/**
 * Wizard state helpers for the Family/Friends Pty registration: empty
 * records, localStorage persistence (field values + step only — NEVER files
 * or signatures) and the calculator → People hand-off.
 */
import {
  LS_CALCULATOR_STORE,
  LS_WIZARD_STORE,
  type CalculatorHandoff,
  type FieldErrors,
  type RegistrationPayload,
  type RegistrationPerson,
} from '../../utils/companyRegistrationShared';

export type WizardStep = 1 | 2 | 3 | 4;

export const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Company',
  2: 'People',
  3: 'Company name',
  4: 'Review and sign',
};

/** A file the user picked or a signature they drew, held in memory only. */
export interface HeldFile {
  blob: Blob;
  contentType: string;
  fileName: string;
  isPdf: boolean;
  /** Object URL for thumbnails (images and signatures). */
  previewUrl: string;
}

/** Keys of the in-memory file store: `<person.key>.idFront|idBack|signature` or `declaration.signature`. */
export type FileStore = Record<string, HeldFile | undefined>;

export const SELECTED = 'selected';

let keySeq = 0;
export function newPersonKey(): string {
  keySeq += 1;
  return `p${Date.now().toString(36)}${keySeq}`;
}

export function emptyPerson(): RegistrationPerson {
  return {
    key: newPersonKey(),
    fullName: '',
    idNumber: '',
    cellphone: '',
    email: '',
    homeAddress: '',
    roles: { director: false, shareholder: false, incorporator: false },
    shares: null,
    shareholdingPct: null,
    idDocType: '',
    creditCheckConsent: false,
    signedAtPlace: '',
    uploads: { idFront: '', idBack: '', signature: '' },
  };
}

export function emptyPayload(): RegistrationPayload {
  const first = emptyPerson();
  first.roles = { director: true, shareholder: false, incorporator: true };
  return {
    company: {
      yearEndMonth: 'February',
      authorisedShares: 1000,
      businessAddress: '',
      postalSameAsBusiness: false,
      postalAddress: '',
    },
    people: [first],
    name: { hasReservation: '', reservedName: '', reservationReference: '', options: ['', '', '', ''] },
    declaration: { accepted: false, fullName: '', signature: '' },
  };
}

// ── localStorage ─────────────────────────────────────────────────────────

interface SavedWizard {
  v: 1;
  step: WizardStep;
  payload: RegistrationPayload;
  savedAtMs: number;
}

function stripUploads(p: RegistrationPayload): RegistrationPayload {
  return {
    ...p,
    people: p.people.map((person) => ({ ...person, uploads: { idFront: '', idBack: '', signature: '' } })),
    declaration: { ...p.declaration, signature: '' },
  };
}

export function loadWizard(): SavedWizard | null {
  try {
    const raw = window.localStorage.getItem(LS_WIZARD_STORE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedWizard>;
    if (!parsed || parsed.v !== 1 || !parsed.payload || !Array.isArray(parsed.payload.people)) return null;
    const base = emptyPayload();
    const p = parsed.payload;
    const payload: RegistrationPayload = {
      company: { ...base.company, ...p.company },
      people: (p.people.length ? p.people : base.people).map((person) => ({
        ...emptyPerson(),
        ...person,
        key: typeof person.key === 'string' && person.key ? person.key : newPersonKey(),
        roles: { ...emptyPerson().roles, ...(person.roles || {}) },
      })),
      name: {
        ...base.name,
        ...p.name,
        options: (Array.isArray(p.name?.options) && p.name.options.length === 4 ? p.name.options : base.name.options) as [string, string, string, string],
      },
      declaration: { ...base.declaration, ...p.declaration },
    };
    const step = ([1, 2, 3, 4] as const).includes(parsed.step as WizardStep) ? (parsed.step as WizardStep) : 1;
    // Files and signatures are never stored: the user re-selects / re-signs.
    return { v: 1, step, payload: stripUploads(payload), savedAtMs: Number(parsed.savedAtMs) || 0 };
  } catch {
    return null;
  }
}

export function saveWizard(step: WizardStep, payload: RegistrationPayload): number {
  const savedAtMs = Date.now();
  try {
    const data: SavedWizard = { v: 1, step, payload: stripUploads(payload), savedAtMs };
    window.localStorage.setItem(LS_WIZARD_STORE, JSON.stringify(data));
  } catch {
    // Private mode / quota: progress just is not saved.
  }
  return savedAtMs;
}

export function clearWizard(): void {
  try {
    window.localStorage.removeItem(LS_WIZARD_STORE);
  } catch {
    /* ignore */
  }
}

export function readHandoff(): CalculatorHandoff | null {
  try {
    const raw = window.localStorage.getItem(LS_CALCULATOR_STORE);
    if (!raw) return null;
    const h = JSON.parse(raw) as CalculatorHandoff;
    if (!h || !Array.isArray(h.people) || typeof h.savedAtMs !== 'number' || h.people.length === 0) return null;
    return h;
  } catch {
    return null;
  }
}

export function writeHandoff(h: CalculatorHandoff): void {
  try {
    window.localStorage.setItem(LS_CALCULATOR_STORE, JSON.stringify(h));
  } catch {
    /* ignore */
  }
}

const isPlaceholderName = (n: string) => !n.trim() || /^person\s*\d+$/i.test(n.trim());

/**
 * Apply a calculator hand-off to the People list: names (unless placeholders)
 * and shareholding % go onto the people in order, new people are appended,
 * every other field the user already filled in is kept. Trailing people the
 * hand-off does not cover are dropped only if they are completely empty.
 */
export function applyHandoff(payload: RegistrationPayload, h: CalculatorHandoff): RegistrationPayload {
  const people = payload.people.map((p) => ({ ...p, roles: { ...p.roles } }));
  h.people.forEach((entry, i) => {
    if (!people[i]) people.push(emptyPerson());
    const p = people[i];
    if (!isPlaceholderName(entry.name)) p.fullName = entry.name.trim();
    p.roles.shareholder = true;
    p.shareholdingPct = typeof entry.shareholdingPct === 'number' && isFinite(entry.shareholdingPct) ? entry.shareholdingPct : null;
  });
  const isBlank = (p: RegistrationPerson) => !p.fullName.trim() && !p.idNumber.trim() && !p.email.trim() && !p.cellphone.trim();
  const trimmed = people.filter((p, i) => i < h.people.length || !isBlank(p));
  if (trimmed[0]) {
    trimmed[0].roles.director = true;
    if (!trimmed.some((p) => p.roles.incorporator)) trimmed[0].roles.incorporator = true;
  }
  return { ...payload, people: trimmed };
}

// ── Errors ───────────────────────────────────────────────────────────────

export function stepForErrorPath(path: string): WizardStep {
  if (path.startsWith('company.')) return 1;
  if (path === 'people' || path.startsWith('people.')) return 2;
  if (path.startsWith('name.')) return 3;
  return 4;
}

export function firstErrorStep(errors: FieldErrors): WizardStep | null {
  const steps = Object.keys(errors).map(stepForErrorPath);
  return steps.length ? (Math.min(...steps) as WizardStep) : null;
}

export const PEOPLE_LIST_ERROR_KEYS = ['people', 'people.directors', 'people.incorporator', 'people.shareholdingTotal', 'people.sharesTotal'];

/** DOM id for the input that owns an error path (used to scroll/focus). */
export function fieldDomId(path: string): string {
  return `cr-${path.replace(/\./g, '-')}`;
}
